"use client"

import { useEffect, useMemo, useState } from "react"
import { useTranslations } from "next-intl"

type JournalConfig = {
  channelId: string | null
  channelUrl: string | null
}

const YOUTUBE_CHANNEL_ID_PATTERN = /^UC[A-Za-z0-9_-]{20,}$/

const YantraVibeJournal = () => {
  const t = useTranslations("Journal")
  const [config, setConfig] = useState<JournalConfig | null>(null)

  useEffect(() => {
    let active = true

    fetch("/api/journal-config", { cache: "no-store" })
      .then((response) => (response.ok ? response.json() : null))
      .then((data: JournalConfig | null) => {
        if (active) setConfig(data)
      })
      .catch(() => {
        if (active) setConfig(null)
      })

    return () => {
      active = false
    }
  }, [])

  const uploadsPlaylistId = useMemo(() => {
    if (!config?.channelId || !YOUTUBE_CHANNEL_ID_PATTERN.test(config.channelId)) {
      return null
    }

    return `UU${config.channelId.slice(2)}`
  }, [config?.channelId])

  return (
    <section className="border-t border-ui-border-base">
      <div className="content-container py-16 small:py-24">
        <div className="mb-8 flex items-end justify-between gap-6">
          <div>
            <p className="mb-2 text-[10px] font-medium uppercase tracking-[0.24em] text-ui-fg-muted">
              {t("eyebrow")}
            </p>
            <h2 className="h3-webs">{t("title")}</h2>
          </div>

          {config?.channelUrl && uploadsPlaylistId ? (
            <a
              href={config.channelUrl}
              target="_blank"
              rel="noreferrer"
              className="hidden text-[11px] uppercase tracking-[0.14em] text-ui-fg-subtle transition-opacity hover:opacity-50 small:inline"
            >
              {t("openChannel")}
            </a>
          ) : null}
        </div>

        {uploadsPlaylistId ? (
          <div className="overflow-hidden rounded-[24px] border border-[#D8C7A6]/60 bg-[#F7F3ED]">
            <div className="aspect-video w-full">
              <iframe
                className="h-full w-full"
                src={`https://www.youtube-nocookie.com/embed/videoseries?list=${uploadsPlaylistId}&rel=0`}
                title={t("playerTitle")}
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
          </div>
        ) : (
          <div className="flex min-h-[280px] items-center justify-center rounded-[24px] border border-[#D8C7A6]/60 bg-[#F7F3ED] px-8 text-center small:min-h-[360px]">
            <div className="max-w-xl">
              <p className="text-lg text-ui-fg-base">{t("comingSoonTitle")}</p>
              <p className="mt-3 text-sm leading-6 text-ui-fg-subtle">
                {t("comingSoonDescription")}
              </p>
            </div>
          </div>
        )}
      </div>
    </section>
  )
}

export default YantraVibeJournal
